import React, { useState, useEffect, useRef } from 'react';
import { X, User, Phone, LogOut, Loader2, CheckCircle, MessageCircle, Bell, Settings, HelpCircle, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useUserProfile } from '../hooks/useUserProfile';
import { PhoneVerificationSection } from './PhoneVerificationSection';
import { NotificationPreferencesSection } from './NotificationPreferencesSection';
import { ProfileDetailsSection } from './ProfileDetailsSection';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignOut: () => void;
  displayName: string;
  userEmail?: string;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  onClose,
  onSignOut,
  displayName,
  userEmail
}) => {
  const { user } = useAuth();
  const { profile, loading: profileLoading, sendVerificationCode, verifyPhoneCode, updatePhoneNumber, refetch } = useUserProfile();
  const [showPhoneEdit, setShowPhoneEdit] = useState(false);
  
  // Swipe to close state
  const panelRef = useRef<HTMLDivElement>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchCurrentX, setTouchCurrentX] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle touch events for swipe to close
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - touchStartX;
    // Only allow swiping to the right (positive diff)
    if (diff > 0) {
      setTouchCurrentX(currentX);
    }
  };

  const handleTouchEnd = () => {
    if (touchStartX !== null && touchCurrentX !== null) {
      const diff = touchCurrentX - touchStartX;
      // If swiped more than 100px to the right, close the modal
      if (diff > 100) {
        onClose();
      }
    }
    setTouchStartX(null);
    setTouchCurrentX(null);
    setIsDragging(false);
  };

  // Calculate transform for swipe animation
  const getSwipeTransform = () => {
    if (touchStartX !== null && touchCurrentX !== null && isDragging) {
      const diff = touchCurrentX - touchStartX;
      if (diff > 0) {
        return `translateX(${diff}px)`;
      }
    }
    return 'translateX(0)';
  };

  if (!isOpen) return null;

  const handleSignOut = () => {
    onClose();
    onSignOut();
  };

  const handlePhoneVerified = () => {
    setShowPhoneEdit(false);
    refetch();
  };

  const handleProfileUpdate = () => {
    refetch();
  };

  const phoneNumber = profile?.phone_number;
  const isPhoneVerified = profile?.phone_number_verified;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Slide-over Panel - higher z-index than header */}
      <div 
        ref={panelRef}
        className="fixed inset-y-0 right-0 z-[101] w-full max-w-md overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: getSwipeTransform(),
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        <div className="flex h-full flex-col bg-white dark:bg-gray-900 shadow-2xl">
          {/* Header - with safe area padding for mobile, ensure it's above page header */}
          <div className="bg-gradient-to-b from-theme-primary to-theme-secondary px-6 py-6 pt-[calc(1.5rem+env(safe-area-inset-top))] relative z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-white/20 p-3 rounded-xl">
                  <User className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">My Profile</h2>
                  <p className="text-white/80 text-sm mt-0.5">
                    Manage your account
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200 -mr-1"
                aria-label="Close profile"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {/* Swipe indicator for mobile */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 md:hidden">
              <div className="w-10 h-1 bg-white/30 rounded-full" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {profileLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-theme-primary" />
              </div>
            ) : (
              <>
                {/* Personal Details Section */}
                <section className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
                      <Settings className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">Personal Details</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Your account information
                      </p>
                    </div>
                  </div>
                  
                  <ProfileDetailsSection
                    initialFirstName={profile?.first_name || ''}
                    initialLastName={profile?.last_name || ''}
                    userEmail={userEmail}
                    onProfileUpdate={handleProfileUpdate}
                  />
                </section>

                {/* Phone Number Section */}
                <section className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-emerald-100 dark:bg-emerald-900/30 p-2 rounded-lg">
                        <MessageCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">WhatsApp Number</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          For pick reminders & updates
                        </p>
                      </div>
                    </div>
                  </div>

                  {showPhoneEdit ? (
                    <PhoneVerificationSection
                      onSendCode={sendVerificationCode}
                      onVerifyCode={verifyPhoneCode}
                      onSuccess={handlePhoneVerified}
                      onCancel={() => setShowPhoneEdit(false)}
                      initialPhoneNumber={phoneNumber}
                    />
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Phone className="h-5 w-5 text-gray-400" />
                        {phoneNumber ? (
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-900 dark:text-white font-medium">
                              {phoneNumber}
                            </span>
                            {isPhoneVerified && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium">
                                <CheckCircle className="h-3 w-3" />
                                Verified
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">
                            Not set
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setShowPhoneEdit(true)}
                        className="text-sm font-medium text-theme-primary hover:text-theme-primary/80 transition-colors"
                      >
                        {phoneNumber ? 'Change' : 'Add'}
                      </button>
                    </div>
                  )}
                </section>

                {/* Notification Preferences Section */}
                <section className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                      <Bell className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">Notifications</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Choose how you want to be notified
                      </p>
                    </div>
                  </div>
                  
                  <NotificationPreferencesSection hasVerifiedPhone={isPhoneVerified} />
                </section>

                {/* Help & Support Section */}
                <section className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-amber-100 dark:bg-amber-900/30 p-2 rounded-lg">
                      <HelpCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">Need Help?</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Get in touch with our support team
                      </p>
                    </div>
                  </div>
                  
                  <a
                    href="mailto:support@footypicks.co.uk"
                    className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-xl font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-all duration-200 border border-amber-200 dark:border-amber-800"
                  >
                    <Mail className="h-4 w-4" />
                    <span>support@footypicks.co.uk</span>
                  </a>
                </section>
              </>
            )}
          </div>

          {/* Footer with Sign Out */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl font-semibold hover:bg-red-100 dark:hover:bg-red-900/40 transition-all duration-200 border border-red-200 dark:border-red-800"
            >
              <LogOut className="h-5 w-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
